import { clsx } from 'clsx'
import { type ComponentProps } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost'
type Size = 'sm' | 'md' | 'lg'

const variants: Record<Variant, string> = {
  primary: 'bg-ink text-bg hover:bg-ink-2 shadow-sm',
  secondary: 'bg-surface text-ink border border-line hover:border-line-strong shadow-sm',
  ghost: 'text-muted hover:text-ink hover:bg-surface-2',
}

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  lg: 'h-11 px-5 text-[15px] gap-2',
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  ...props
}: ComponentProps<'button'> & { variant?: Variant; size?: Size }) {
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap select-none',
        'transition-[background-color,border-color,color,transform] duration-150 ease-(--ease-spring)',
        'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function IconButton({
  label,
  className,
  ...props
}: ComponentProps<'button'> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx(
        'inline-grid size-8 place-items-center rounded-md text-muted transition-colors',
        'hover:bg-surface-2 hover:text-ink data-[state=open]:bg-surface-2 data-[state=open]:text-ink',
        className,
      )}
      {...props}
    />
  )
}
